import InteractiveChatHistory, {
  IAvatarChatHistoryMessage
} from "@components/InteractiveChat/components/InteractiveChatHistory/InteractiveChatHistory";
import { CONVERSATION_RETENTION_DAYS } from "@config";
import { Box, Typography } from "@mui/material";
import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { InteractiveChatTextInput } from "../../InteractiveChatTextInput";

interface IAvatarChatBoxProps {
  messages: IAvatarChatHistoryMessage[];
  disabled?: boolean;
  loading?: boolean;
  text: string;
  setText: (value: string) => void;
  handleSubmitMessage: () => void;
  handleRecord: (blob: Blob) => void;
  audioBlob?: Blob | null;
  handleDeleteAudio: () => void;
  handleStop?: () => void;
  isExpired?: boolean;
  expirationDays?: number;
  onSourcesClick?: (sourceIds: string[]) => void;
}

const InteractiveChatBox = ({
  messages,
  disabled = false,
  loading = false,
  text,
  setText,
  handleSubmitMessage,
  handleRecord,
  audioBlob,
  handleDeleteAudio,
  handleStop,
  isExpired = false,
  expirationDays = CONVERSATION_RETENTION_DAYS,
  onSourcesClick
}: IAvatarChatBoxProps) => {
  const { t } = useTranslation();
  const chatHistoryRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollToBottom = () => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    };

    scrollToBottom();

    const timeoutId = setTimeout(scrollToBottom, 100);

    return () => clearTimeout(timeoutId);
  }, [messages]);

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "white",
          overflow: "auto",
          position: "relative",
          pb: { xs: "140px", sm: "160px" }
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            backgroundColor: "white"
          }}
          ref={chatHistoryRef}
        >
          <InteractiveChatHistory
            messages={messages}
            noSidePadding
            onSourcesClick={onSourcesClick}
          />
        </Box>
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          display: "flex",
          justifyContent: "center",
          zIndex: 1000
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 800,
            px: { xs: 2, sm: 4 },
            pt: { xs: 1, sm: 2 },
            pb: { xs: 2, sm: 2 },
            backgroundColor: "white"
          }}
        >
          {/* Expiration notice */}
          {isExpired && (
            <Box
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.customColors.hoverBackground
              }}
            >
              <Typography
                sx={{
                  fontFamily: (theme) => theme.typography.fontFamily,
                  fontSize: "14px",
                  fontWeight: 325,
                  lineHeight: "18px",
                  letterSpacing: "0%",
                  color: (theme) => theme.palette.customColors.textPrimary,
                  textAlign: "center"
                }}
              >
                {t("chatbot.conversation_expired", {
                  days: expirationDays
                })}
              </Typography>
            </Box>
          )}

          <InteractiveChatTextInput
            disabled={disabled}
            value={text}
            onChange={setText}
            label="Chat"
            loading={loading}
            placeholder={t("chatbot.actions.interact")}
            onSubmit={handleSubmitMessage}
            onRecord={handleRecord}
            audioBlob={audioBlob}
            onDeleteAudio={handleDeleteAudio}
            onStop={handleStop}
          />

          {/* Disclaimer text */}
          {!isExpired && (
            <Box sx={{ px: 0, mt: 1 }}>
              <Typography
                sx={{
                  fontFamily: (theme) => theme.typography.fontFamily,
                  fontSize: "14px",
                  fontWeight: 325,
                  lineHeight: "14px",
                  letterSpacing: "0%",
                  color: (theme) => theme.palette.customColors.textTertiary,
                  textAlign: "center"
                }}
              >
                {t("chatbot.disclaimer")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default InteractiveChatBox;
